import _ from 'lodash';
import moment from 'moment';
import { call, put, takeLatest } from 'redux-saga/effects';
import { createActionSetError, TErrorAction } from '../actions/errorActions';
import { TAction } from '../actions/types/action';
import { apiServiceAsync } from '../services/apiService';
import { actions as messageCenterActions } from '../messageCenter/modules';

//
// Actions
//

type TGroupCharterClientState = {};

export type TListThreadPostsRequestParam = {
  slug?: string;
  id?: number;
};

export type TActions = {
  showLoadingSpinner(show: boolean): TAction<boolean>;
  showError(info: any): TErrorAction;

  // list all thread posts by slug (e.g. 'global-coverage-castle-nathria')
  setListThreadPostsRequested(param: TListThreadPostsRequestParam): TAction<any>;
  setListThreadPostsSucceeded(results: any): TAction<any>;
  setListThreadPostsFailed(error: any): TAction<any>;

  // peek the top threads by slug (e.g. 'global-coverage-castle-nathria')
  setPeekThreadPostsRequested(threadSlug: string): TAction<any>;
  setPeekThreadPostsSucceeded(results: any): TAction<any>;
  setPeekThreadPostsFailed(error: any): TAction<any>;

  // refresh the posts in a thread that is currently being viewed
  setRefreshThreadPostsRequested(threadId: number, lastRefreshTime: string): TAction<any>;
  setRefreshThreadPostsSucceeded(threadId: number, results: any): TAction<any>;
  setRefreshThreadPostsFailed(threadId: number, error: any): TAction<any>;
};

export const actionTypes = {
  SHOW_LOADING_SPINNER: 'THREADS/SHOW_LOADING_SPINNER',

  SET_LIST_THREAD_POSTS_REQUESTED: 'THREADS/LIST_THREAD_POSTS_REQUESTED',
  SET_LIST_THREAD_POSTS_SUCCEEDED: 'THREADS/LIST_THREAD_POSTS_SUCCEEDED',
  SET_LIST_THREAD_POSTS_FAILED: 'THREADS/LIST_THREAD_POSTS_FAILED',

  SET_PEEK_THREAD_POSTS_REQUESTED: 'THREADS/PEEK_THREAD_POSTS_REQUESTED',
  SET_PEEK_THREAD_POSTS_SUCCEEDED: 'THREADS/PEEK_THREAD_POSTS_SUCCEEDED',
  SET_PEEK_THREAD_POSTS_FAILED: 'THREADS/PEEK_THREAD_POSTS_FAILED',

  SET_REFRESH_THREAD_POSTS_REQUESTED: 'THREADS/REFRESH_THREAD_POSTS_REQUESTED',
  SET_REFRESH_THREAD_POSTS_SUCCEEDED: 'THREADS/REFRESH_THREAD_POSTS_SUCCEEDED',
  SET_REFRESH_THREAD_POSTS_FAILED: 'THREADS/REFRESH_THREAD_POSTS_FAILED'
};

export const actions: TActions = {
  showLoadingSpinner(show: boolean) {
    return { type: actionTypes.SHOW_LOADING_SPINNER, payload: show };
  },

  showError: createActionSetError,

  //
  // List the posts in a thread
  //
  setListThreadPostsRequested(param: TListThreadPostsRequestParam) {
    return { type: actionTypes.SET_LIST_THREAD_POSTS_REQUESTED, payload: param };
  },

  setListThreadPostsSucceeded(results: any) {
    return { type: actionTypes.SET_LIST_THREAD_POSTS_SUCCEEDED, payload: { results } };
  },

  setListThreadPostsFailed(error: any) {
    return { type: actionTypes.SET_LIST_THREAD_POSTS_FAILED, payload: error };
  },

  //
  // Peek the posts in a thread
  //
  setPeekThreadPostsRequested(threadSlug: string) {
    return { type: actionTypes.SET_PEEK_THREAD_POSTS_REQUESTED, payload: { slug: threadSlug } };
  },

  setPeekThreadPostsSucceeded(results: any) {
    return { type: actionTypes.SET_PEEK_THREAD_POSTS_SUCCEEDED, payload: { results } };
  },

  setPeekThreadPostsFailed(error: any) {
    return { type: actionTypes.SET_PEEK_THREAD_POSTS_FAILED, payload: error };
  },

  //
  // Refresh the posts in a thread
  //
  setRefreshThreadPostsRequested(threadId: number, lastRefreshTime: string) {
    return { type: actionTypes.SET_REFRESH_THREAD_POSTS_REQUESTED, payload: { threadId, lastRefreshTime } };
  },

  setRefreshThreadPostsSucceeded(threadId: number, results: any) {
    return { type: actionTypes.SET_REFRESH_THREAD_POSTS_SUCCEEDED, payload: { threadId, results } };
  },

  setRefreshThreadPostsFailed(threadId: number, error: any) {
    return { type: actionTypes.SET_REFRESH_THREAD_POSTS_FAILED, payload: { threadId, error } };
  }
};

//
// Reducer
//

function getInitialState() {
  return {
    listThreadPosts: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    peekThreadPosts: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    refreshThreadPosts: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    // the currently peeked posts
    peekedPosts: [],

    // the active thread being viewed
    thread: null,
    posts: [],
    postsById: {},
    refreshedAt: '',
    recipients: []
  };
}

export function reducer(state: any = getInitialState(), action: TAction<any>): Partial<TGroupCharterClientState> {
  switch (action.type) {
    case actionTypes.SHOW_LOADING_SPINNER: {
      return {
        ...state,
        isLoading: action.payload
      };
    }

    case actionTypes.SET_LIST_THREAD_POSTS_REQUESTED: {
      return {
        ...state,
        listThreadPosts: {
          ...state.listThreadPosts,
          isRequested: true,
          isSucceeded: false,
          isErrored: false,
          error: null
        }
      };
    }

    case actionTypes.SET_LIST_THREAD_POSTS_SUCCEEDED: {
      const {
        participantProfiles,
        thread,
        recipients,
        posts,
        refreshed_at: refreshedAt,
        maxPostIdViewed
      } = action.payload.results;

      return {
        ...state,

        listThreadPosts: {
          ...state.listThreadPosts,
          isRequested: false,
          isSucceeded: true,
          isErrored: false,
          error: null
        },

        thread: {
          ...state.thread,
          ...thread,
          participantProfiles
        },

        recipients,

        posts,
        postsById: _.groupBy(posts, 'id'),
        maxPostIdViewed,
        refreshedAt
      };
    }

    case actionTypes.SET_LIST_THREAD_POSTS_FAILED: {
      return {
        ...state,
        listThreadPosts: {
          ...state.listThreadPosts,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        },

        thread: null,
        posts: [],
        postsById: {}
      };
    }

    //
    // Peek thread
    //
    case actionTypes.SET_PEEK_THREAD_POSTS_REQUESTED: {
      return {
        ...state,
        peekThreadPosts: {
          ...state.peekThreadPosts,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case actionTypes.SET_PEEK_THREAD_POSTS_SUCCEEDED: {
      const { posts } = action.payload.results;

      return {
        ...state,

        peekThreadPosts: {
          ...state.peekThreadPosts,
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        },

        peekedPosts: posts
      };
    }

    case actionTypes.SET_PEEK_THREAD_POSTS_FAILED: {
      return {
        ...state,
        peekThreadPosts: {
          ...state.peekThreadPosts,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    //
    // Refresh thread
    //

    case actionTypes.SET_REFRESH_THREAD_POSTS_REQUESTED: {
      return {
        ...state,
        refreshThreadPosts: {
          isRequested: true,
          isSucceeded: false,
          isErrored: false,
          ...action.payload
        }
      };
    }

    case actionTypes.SET_REFRESH_THREAD_POSTS_SUCCEEDED: {
      const { thread, posts, refreshed_at: refreshedAt } = action.payload.results;
      const newState = {
        ...state,
        refreshThreadPosts: {
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        }
      };

      if (state.thread && state.thread.id === thread.id) {
        // for each new posts see if it already exists, and if so we just update it
        const newPosts = [];
        const updatePosts: any = {};

        // categorize the posts into ones that need updating and new ones
        for (const post of posts) {
          if (state.postsById[post.id]) {
            updatePosts[post.id] = post;
          } else {
            newPosts.push(post);
          }
        }

        let postsNeedSorting = false;
        if (state.posts.length > 0) {
          const priorNewestPost = state.posts[0].published_at;
          const priorNewestTime = moment.utc(priorNewestPost.published_at);
          for (const post of newPosts) {
            if (moment.utc(post.published_at) < priorNewestTime) {
              // a post from the past was added, so we will need to sort the full list of posts to ensure everything is in order
              postsNeedSorting = true;
              break;
            }
          }
        }

        // if we got an update for a post just override it
        newState.posts = [...newPosts, ...state.posts.map((post: any) => updatePosts[post.id] || post)];

        if (postsNeedSorting) {
          newState.posts.sort((a: any, b: any) => moment.utc(b.published_at).diff(moment.utc(a.published_at)));
        }

        newState.postsById = _.groupBy(newState.posts, 'id');
        newState.refreshedAt = refreshedAt;

        // update the posts that just need updating
        for (const post of updatePosts) {
          Object.assign(newState.postsById[post.id], post);
        }
      } else if (!state.thread || thread.id !== state.thread.id) {
        // got a refresh for the thread that wasn't loaded so just override it
        newState.thread = thread;
        newState.posts = posts;
        newState.postsById = _.groupBy(posts, 'id');
        newState.refreshedAt = refreshedAt;
      }

      return newState;
    }

    case actionTypes.SET_REFRESH_THREAD_POSTS_FAILED: {
      return {
        ...state,
        refreshThreadPosts: {
          ...state.refreshThreadPosts,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }
  }

  return state;
}

//
// Sagas
//

//
// list thread posts
//
function* listThreadPosts(action: TAction<any>): any {
  const { slug, id } = action.payload;
  try {
    const results = yield call(apiServiceAsync.listThreadPosts, { slug, id });
    yield put(messageCenterActions.setMessageCenterThreadRead(id));
    yield put(actions.setListThreadPostsSucceeded(results));
  } catch (e) {
    yield put(actions.setListThreadPostsFailed(e.toString()));
  }
}

function* watchListThreadPostsRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.SET_LIST_THREAD_POSTS_REQUESTED, listThreadPosts);
}

//
// peek thread posts
//
function* peekThreadPosts(action: TAction<any>): any {
  const { slug } = action.payload;
  try {
    const results = yield call(apiServiceAsync.peekThreadPosts, { slug });
    yield put(actions.setPeekThreadPostsSucceeded(results));
  } catch (e) {
    yield put(actions.setPeekThreadPostsFailed(e.toString()));
  }
}

function* watchPeekThreadPostsRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.SET_PEEK_THREAD_POSTS_REQUESTED, peekThreadPosts);
}

//
// refresh thread posts
//
function* refreshThreadPosts(action: TAction<any>): any {
  const { threadId, lastRefreshTime } = action.payload;
  try {
    const results = yield call(apiServiceAsync.refreshThreadPosts, { id: threadId, lastRefreshTime });
    yield put(actions.setRefreshThreadPostsSucceeded(threadId, results));
  } catch (e) {
    yield put(actions.setRefreshThreadPostsFailed(threadId, e.toString()));
  }
}

function* watchRefreshThreadPostsRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.SET_REFRESH_THREAD_POSTS_REQUESTED, refreshThreadPosts);
}

//
// saga exports
//
export const sagas = [
  watchListThreadPostsRequested(),
  watchRefreshThreadPostsRequested(),
  watchPeekThreadPostsRequested()
];
